import { useEffect, useState } from "react";
import { MessagesSquare, Plus, ArrowLeft, Send, MessageCircle, Trash2, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { btn } from "@/components/Page";
import { useAuth } from "@/context/AuthContext";
import ConfirmDialog from "@/components/ConfirmDialog";
import { linkifyUrls } from "@/lib/textUtils";

function timeAgo(iso) {
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

export default function MessageBoard({ apiPrefix = "/board", allianceId, title = "Message Board", emptyMessage = "No threads yet.", readOnly = false }) {
  const { user } = useAuth();
  const [threads, setThreads] = useState(null);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [newCount, setNewCount] = useState(0);
  const [active, setActive] = useState(null); // thread id
  const [detail, setDetail] = useState(null); // {thread, replies, is_admin}
  const [composing, setComposing] = useState(false);
  const [titleInput, setTitleInput] = useState("");
  const [body, setBody] = useState("");
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingThread, setEditingThread] = useState(false);
  const [etTitle, setEtTitle] = useState("");
  const [etBody, setEtBody] = useState("");
  const [editingReply, setEditingReply] = useState(null);
  const [erText, setErText] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);

  const loadThreads = (p = 1) => {
    const url = allianceId 
      ? `${apiPrefix}/threads?page=${p}&alliance_id=${allianceId}`
      : `${apiPrefix}/threads?page=${p}`;
    api.get(url).then((r) => {
      setThreads(r.data.threads); setPages(r.data.pages || 1); setTotal(r.data.total || 0); setPage(r.data.page || p);
    }).catch(() => setThreads([]));
  };
  const openThread = async (id) => {
    setActive(id); setDetail(null);
    try { const { data } = await api.get(`${apiPrefix}/threads/${id}`); setDetail(data); }
    catch (e) { toast.error(apiErr(e)); setActive(null); }
  };

  useEffect(() => {
    // Only fetch badges for the main arena board
    if (apiPrefix === "/board") {
      api.get("/badges").then((r) => setNewCount(r.data.board_unread || 0)).catch(() => {});
      // Opening the board clears the new-post alert on the Arena Chat nav item.
      api.post("/board/seen").then(() => window.dispatchEvent(new Event("lanista:refresh-badges"))).catch(() => {});
    }
    loadThreads(1);
  }, [apiPrefix]);

  const createThread = async (e) => {
    e.preventDefault();
    if (!titleInput.trim()) return;
    setBusy(true);
    try {
      await api.post(`${apiPrefix}/threads`, { title: titleInput.trim(), body: body.trim() });
      setTitleInput(""); setBody(""); setComposing(false);
      await loadThreads(1);
      toast.success("Thread posted");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`${apiPrefix}/threads/${active}/replies`, { text: reply.trim() });
      setReply("");
      setDetail((d) => ({ ...d, replies: [...d.replies, data], thread: { ...d.thread, reply_count: d.thread.reply_count + 1 } }));
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const deleteThread = async (id) => {
    setConfirmDelete({
      title: "Delete Thread",
      message: "Delete this thread and all its replies? This cannot be undone.",
      confirmLabel: "Delete",
      danger: true,
      onConfirm: async () => {
        try {
          await api.delete(`${apiPrefix}/threads/${id}`);
          setThreads((t) => (t || []).filter((x) => x.id !== id));
          if (active === id) { setActive(null); setDetail(null); }
          toast.success("Thread deleted");
          setConfirmDelete(null);
        } catch (e) { toast.error(apiErr(e)); }
      }
    });
  };

  const deleteReply = async (replyId) => {
    setConfirmDelete({
      title: "Delete Reply",
      message: "Delete this reply? This cannot be undone.",
      confirmLabel: "Delete",
      danger: true,
      onConfirm: async () => {
        try {
          await api.delete(`${apiPrefix}/replies/${replyId}`);
          setDetail((d) => ({ ...d, replies: d.replies.filter((r) => r.id !== replyId) }));
          toast.success("Reply deleted");
          setConfirmDelete(null);
        } catch (e) { toast.error(apiErr(e)); }
      }
    });
  };

  const startEditThread = () => { setEtTitle(detail.thread.title); setEtBody(detail.thread.body || ""); setEditingThread(true); };
  const saveThreadEdit = async () => {
    if (!etTitle.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.put(`${apiPrefix}/threads/${active}`, { title: etTitle.trim(), body: etBody.trim() });
      setDetail(data); setEditingThread(false);
      toast.success("Thread updated");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const startEditReply = (r) => { setEditingReply(r.id); setErText(r.text); };
  const saveReplyEdit = async (id) => {
    if (!erText.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.put(`${apiPrefix}/replies/${id}`, { text: erText.trim() });
      setDetail((d) => ({ ...d, replies: d.replies.map((x) => (x.id === id ? data : x)) }));
      setEditingReply(null);
      toast.success("Reply updated");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  // ---- Thread detail view ----
  if (active) {
    return (
      <section data-testid="board-thread-view" className="border border-amber-900/30 bg-zinc-950/60">
        <div className="flex items-center gap-3 border-b border-amber-900/30 px-4 py-3">
          <button data-testid="board-back-btn" onClick={() => { setActive(null); setDetail(null); setEditingThread(false); loadThreads(page); }}
            className="text-zinc-400 hover:text-amber-400 flex items-center gap-1.5 text-sm"><ArrowLeft size={16} /> Back</button>
          {detail && !readOnly && !editingThread && (detail.thread.user_id === user?.user_id || user?.is_admin || detail.is_leader) && (
            <button data-testid="board-thread-edit" onClick={startEditThread} className="ml-auto text-zinc-500 hover:text-amber-400 flex items-center gap-1.5 text-sm" title="Edit thread"><Pencil size={14} /> Edit</button>
          )}
          {detail && !readOnly && (user?.is_admin || detail.is_leader || detail.thread.user_id === user?.user_id) && (
            <button data-testid="board-thread-delete" onClick={() => deleteThread(active)} 
              className={`${detail && !editingThread && (detail.thread.user_id === user?.user_id || user?.is_admin || detail.is_leader) ? "" : "ml-auto"} text-zinc-500 hover:text-red-400 flex items-center gap-1.5 text-sm`} 
              title={user?.is_admin ? "Delete thread (admin)" : detail.is_leader ? "Delete thread (leader)" : "Delete thread"}>
              <Trash2 size={14} /> Delete
            </button>
          )}
        </div>
        {!detail ? <p className="text-zinc-600 text-sm p-6">Loading thread…</p> : (
          <div className="p-5">
            {editingThread ? (
              <div data-testid="board-thread-edit-form" className="mb-6 space-y-3">
                <input data-testid="board-edit-title-input" value={etTitle} onChange={(e) => setEtTitle(e.target.value)} maxLength={120}
                  className="w-full bg-zinc-900 border border-amber-900/40 px-3 py-2.5 text-zinc-100 text-lg font-cinzel focus:border-amber-600 outline-none" />
                <textarea data-testid="board-edit-body-input" value={etBody} onChange={(e) => setEtBody(e.target.value)} maxLength={2000} rows={4}
                  placeholder="Body (optional)"
                  className="w-full bg-zinc-900 border border-amber-900/40 px-3 py-2.5 text-zinc-100 text-sm focus:border-amber-600 outline-none resize-none" />
                <div className="flex justify-end gap-2">
                  <button data-testid="board-edit-thread-cancel" onClick={() => setEditingThread(false)} disabled={busy} className={btn("ghost") + " px-4 py-1.5 text-xs"}><X size={14} /> Cancel</button>
                  <button data-testid="board-edit-thread-save" onClick={saveThreadEdit} disabled={busy || !etTitle.trim()} className={btn() + " px-4 py-1.5 text-xs"}><Check size={14} /> Save</button>
                </div>
              </div>
            ) : (
              <>
                <h3 className="font-cinzel text-xl text-amber-200">{detail.thread.is_blocked ? <span className="text-zinc-600 italic">-blocked-</span> : detail.thread.title}</h3>
                <p className="text-[11px] text-zinc-500 mt-1 mb-4">by <span className="text-zinc-400">{detail.thread.is_blocked ? <span className="italic">-blocked-</span> : detail.thread.name}</span> · {timeAgo(detail.thread.created_at)}{detail.thread.edited_at && <span className="text-zinc-600 italic"> · edited</span>}</p>
                {detail.thread.body && !detail.thread.is_blocked && <p className="text-sm text-zinc-300 whitespace-pre-wrap border-l-2 border-amber-800/50 pl-4 mb-6">{linkifyUrls(detail.thread.body, `thread-${detail.thread.id}`)}</p>}
                {detail.thread.is_blocked && <p className="text-sm text-zinc-600 italic whitespace-pre-wrap border-l-2 border-zinc-800 pl-4 mb-6">-blocked-</p>}
              </>
            )}

            <div className="space-y-3 mb-5" data-testid="board-replies">
              {detail.replies.length === 0 ? (
                <p className="text-zinc-600 text-sm italic">No replies yet. Be the first to respond.</p>
              ) : detail.replies.map((r) => {
                const isBlocked = r.is_blocked === true;
                return (
                <div key={r.id} data-testid={`board-reply-${r.id}`} className={`border px-4 py-3 ${isBlocked ? "bg-zinc-950 border-zinc-800" : "bg-zinc-900 border-zinc-800"}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[11px] font-cinzel uppercase tracking-wide ${
                      isBlocked ? "text-zinc-600 italic" :
                      r.user_id === user?.user_id ? "text-amber-400" : "text-zinc-400"
                    }`}>{isBlocked ? "-blocked-" : (r.user_id === user?.user_id ? "You" : r.name)}</span>
                    <span className="text-[10px] text-zinc-600">{timeAgo(r.created_at)}{r.edited_at && <span className="italic"> · edited</span>}</span>
                    {!isBlocked && editingReply !== r.id && (r.user_id === user?.user_id || user?.is_admin || detail.is_leader) && (
                      <button data-testid={`board-reply-edit-${r.id}`} onClick={() => startEditReply(r)} className="text-zinc-600 hover:text-amber-400" title="Edit reply"><Pencil size={12} /></button>
                    )}
                    {!isBlocked && (user?.is_admin || detail.is_leader) && <button data-testid={`board-reply-delete-${r.id}`} onClick={() => deleteReply(r.id)} className="text-zinc-600 hover:text-red-400" title={user?.is_admin ? "Delete reply (admin)" : "Delete reply (leader)"}><Trash2 size={12} /></button>}
                  </div>
                  {editingReply === r.id ? (
                    <div data-testid={`board-reply-edit-form-${r.id}`} className="space-y-2">
                      <textarea data-testid={`board-reply-edit-input-${r.id}`} value={erText} onChange={(e) => setErText(e.target.value)} maxLength={2000} rows={3}
                        className="w-full bg-zinc-950 border border-amber-900/40 px-3 py-2 text-zinc-100 text-sm focus:border-amber-600 outline-none resize-none" />
                      <div className="flex justify-end gap-2">
                        <button data-testid={`board-reply-edit-cancel-${r.id}`} onClick={() => setEditingReply(null)} disabled={busy} className={btn("ghost") + " px-3 py-1 text-xs"}><X size={13} /> Cancel</button>
                        <button data-testid={`board-reply-edit-save-${r.id}`} onClick={() => saveReplyEdit(r.id)} disabled={busy || !erText.trim()} className={btn() + " px-3 py-1 text-xs"}><Check size={13} /> Save</button>
                      </div>
                    </div>
                  ) : (
                    <p className={`text-sm whitespace-pre-wrap break-words ${isBlocked ? "text-zinc-600 italic" : "text-zinc-200"}`}>
                      {isBlocked ? "-blocked-" : linkifyUrls(r.text, `reply-${r.id}`)}
                    </p>
                  )}
                </div>
                );
              })}            </div>

            <form onSubmit={sendReply} className="flex gap-2">
              <input data-testid="board-reply-input" value={reply} onChange={(e) => setReply(e.target.value)} maxLength={2000}
                placeholder="Write a reply…"
                className="flex-1 bg-zinc-900 border border-amber-900/40 px-4 py-3 text-zinc-100 placeholder-zinc-600 focus:border-amber-600 outline-none" />
              <button data-testid="board-reply-send" type="submit" disabled={busy || !reply.trim()} className={btn() + " px-5"}><Send size={16} /></button>
            </form>
          </div>
        )}
      </section>
    );
  }

  // ---- Thread list view ----
  return (
    <>
    <section data-testid="message-board">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-cinzel text-amber-300 uppercase tracking-wide text-base flex items-center gap-2">
          <MessagesSquare size={18} /> {title}
          {newCount > 0 && (
            <span data-testid="board-new-badge" className="min-w-[18px] h-[18px] px-1.5 inline-flex items-center justify-center rounded-full bg-amber-500 text-zinc-950 text-[10px] font-bold normal-case tracking-normal">
              {newCount > 9 ? "9+" : newCount} new
            </span>
          )}
        </h2>
        {!readOnly && (
          <button data-testid="board-new-thread-btn" onClick={() => setComposing((c) => !c)} className={btn("ghost") + " px-3 py-1.5 text-xs"}><Plus size={14} /> Start a thread</button>
        )}
      </div>

      {composing && !readOnly && (
        <form onSubmit={createThread} data-testid="board-compose" className="border border-amber-900/40 bg-zinc-950/60 p-4 mb-4 space-y-3">
          <input data-testid="board-title-input" value={titleInput} onChange={(e) => setTitleInput(e.target.value)} maxLength={120}
            placeholder="Thread title…"
            className="w-full bg-zinc-900 border border-amber-900/40 px-3 py-2.5 text-zinc-100 placeholder-zinc-600 focus:border-amber-600 outline-none" />
          <textarea data-testid="board-body-input" value={body} onChange={(e) => setBody(e.target.value)} maxLength={2000} rows={3}
            placeholder="Say something… (optional)"
            className="w-full bg-zinc-900 border border-amber-900/40 px-3 py-2.5 text-zinc-100 placeholder-zinc-600 focus:border-amber-600 outline-none resize-none" />
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setComposing(false)} className={btn("ghost") + " px-4 py-1.5 text-xs"}>Cancel</button>
            <button data-testid="board-post-thread-btn" type="submit" disabled={busy || !titleInput.trim()} className={btn() + " px-4 py-1.5 text-xs"}>Post thread</button>
          </div>
        </form>
      )}

      <div className="border border-amber-900/30 bg-zinc-950/60 divide-y divide-zinc-900" data-testid="board-thread-list">
        {threads === null ? (
          <p className="text-zinc-600 text-sm p-5">Loading threads…</p>
        ) : threads.length === 0 ? (
          <p className="text-zinc-600 text-sm p-6 text-center">{emptyMessage}</p>
        ) : threads.map((t) => (
          <div key={t.id} className="flex items-center hover:bg-zinc-900/60 transition-colors">
            <button data-testid={`board-thread-${t.id}`} onClick={() => openThread(t.id)}
              className="flex-1 min-w-0 text-left px-4 py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-zinc-100 truncate">{t.title}</p>
                <p className="text-[11px] text-zinc-500">by {t.name} · {timeAgo(t.last_activity)}</p>
              </div>
              <span className="flex items-center gap-1 text-xs text-zinc-500 shrink-0"><MessageCircle size={13} /> {t.reply_count}</span>
            </button>
            {user?.is_admin && <button data-testid={`board-thread-delete-${t.id}`} onClick={(e) => { e.stopPropagation(); deleteThread(t.id); }} className="px-3 self-stretch text-zinc-600 hover:text-red-400" title="Delete thread (admin)"><Trash2 size={14} /></button>}
          </div>
        ))}
      </div>

      {pages > 1 && (
        <div data-testid="board-pagination" className="flex items-center justify-center gap-3 mt-3">
          <button data-testid="board-prev-btn" disabled={page <= 1} onClick={() => loadThreads(page - 1)}
            className={btn("ghost") + " px-3 py-1.5 text-xs disabled:opacity-40 disabled:cursor-not-allowed"}>Prev</button>
          <span className="text-xs text-zinc-500" data-testid="board-page-indicator">Page {page} of {pages} · {total} threads</span>
          <button data-testid="board-next-btn" disabled={page >= pages} onClick={() => loadThreads(page + 1)}
            className={btn("ghost") + " px-3 py-1.5 text-xs disabled:opacity-40 disabled:cursor-not-allowed"}>Next</button>
        </div>
      )}
    </section>
    
    {confirmDelete && (
      <ConfirmDialog
        state={confirmDelete}
        onClose={() => setConfirmDelete(null)}
      />
    )}
  </>
  );
}
